import { Suspense, lazy, memo, useRef, useCallback, useEffect, useState } from 'react';

const Spline = lazy(() => import('@splinetool/react-spline'));

interface SplineBackgroundProps {
  onLoad?: () => void;
}

const SplineBackground = ({ onLoad: externalOnLoad }: SplineBackgroundProps) => {
  const splineRef = useRef<any>(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleLoad = useCallback((splineApp: any) => {
    splineRef.current = splineApp;

    // The Spline scene has a built-in "Follow Mouse" behavior on the robot head.
    // It works by reading native pointer events on the canvas. Since our parent
    // div is pointer-events-none (to keep scrolling/clicking working on content),
    // the canvas never receives native events. Fix: give the canvas itself
    // pointer-events-auto so Spline's internal Follow Mouse behavior gets real events.
    if (splineApp?.canvas) {
      const canvas = splineApp.canvas as HTMLElement;
      canvas.style.pointerEvents = 'auto';
    }

    if (externalOnLoad) externalOnLoad();
  }, [externalOnLoad]);

  // Forward global mouse events to the Spline canvas as real native pointer events.
  // This is needed because the canvas sits inside a pointer-events-none container,
  // so window-level events never bubble into it naturally.
  useEffect(() => {
    const forwardMouseMove = (e: MouseEvent) => {
      const canvas = splineRef.current?.canvas as HTMLElement | undefined;
      if (!canvas) return;

      // Dispatch a real native PointerEvent (Spline listens to pointermove internally)
      canvas.dispatchEvent(new PointerEvent('pointermove', {
        bubbles: false,
        cancelable: true,
        clientX: e.clientX,
        clientY: e.clientY,
        screenX: e.screenX,
        screenY: e.screenY,
        movementX: e.movementX,
        movementY: e.movementY,
        pointerId: 1,
        pointerType: 'mouse',
        isPrimary: true,
        view: window,
      }));

      // Also dispatch mousemove as a fallback
      canvas.dispatchEvent(new MouseEvent('mousemove', {
        bubbles: false,
        cancelable: true,
        clientX: e.clientX,
        clientY: e.clientY,
        screenX: e.screenX,
        screenY: e.screenY,
        movementX: e.movementX,
        movementY: e.movementY,
        view: window,
      }));
    };

    window.addEventListener('mousemove', forwardMouseMove, { passive: true });
    return () => window.removeEventListener('mousemove', forwardMouseMove);
  }, []);

  return (
    <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
      <div className="absolute inset-0 w-full h-full opacity-80 mix-blend-screen overflow-hidden">
        <Suspense fallback={null}>
          <Spline
            className="w-full h-full"
            scene="https://prod.spline.design/UbM7F-HZcyTbZ4y3/scene.splinecode"
            onLoad={handleLoad}
          />
        </Suspense>
      </div>
    </div>
  );
};

export default memo(SplineBackground);
